import { format } from "date-fns";
import { CommandXFooter } from "./CommandXFooter";

const fmt = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);

export interface COPrintLineItem {
  line_number: number;
  description: string;
  quantity: number;
  unit: string;
  labor_unit_price: number;
  material_unit_price: number;
  unit_price: number;
  amount: number;
}

interface ChangeOrderPrintData {
  number: string;
  description?: string | null;
  notes?: string | null;
  amount: number;
  status: string;
  created_at: string;
  project_name?: string;
  project_number?: string;
  company_name?: string;
  company_logo_url?: string | null;
  job_order_number?: string | null;
  work_order_number?: string | null;
  approved_at?: string | null;
  line_items?: COPrintLineItem[];
}

export const ChangeOrderPrintTemplate = ({ data }: { data: ChangeOrderPrintData }) => (
  <div className="print-template p-8 max-w-[8.5in] mx-auto font-sans text-sm leading-relaxed">
    <div className="flex justify-between items-start border-b pb-4 mb-6">
      <div>
        {data.company_logo_url && (
          <img src={data.company_logo_url} alt={data.company_name ?? "Company"} style={{ maxHeight: "40px", objectFit: "contain", marginBottom: "4px" }} />
        )}
        <h1 className="text-2xl font-bold tracking-tight" style={{ color: "#111" }}>CHANGE ORDER</h1>
        {data.company_name && (
          <p className="text-base font-medium mt-1" style={{ color: "#333" }}>{data.company_name}</p>
        )}
      </div>
      <div className="text-right">
        <p className="text-lg font-bold font-mono" style={{ color: "#111" }}>{data.number}</p>
        <p style={{ color: "#666" }}>Date: {format(new Date(data.created_at), "MMMM d, yyyy")}</p>
        <p className="mt-1 inline-block px-2 py-0.5 rounded text-xs font-semibold uppercase" style={{ background: "#f3f4f6", color: "#374151" }}>
          {data.status}
        </p>
      </div>
    </div>

    <div className="grid grid-cols-2 gap-8 mb-8">
      <div>
        <p className="text-xs uppercase tracking-wider font-semibold mb-1" style={{ color: "#999" }}>Project</p>
        <p className="font-medium" style={{ color: "#111" }}>
          {data.project_name || "—"}
          {data.project_number && <span className="text-xs ml-1" style={{ color: "#666" }}>({data.project_number})</span>}
        </p>
      </div>
      <div>
        <p className="text-xs uppercase tracking-wider font-semibold mb-1" style={{ color: "#999" }}>Amount</p>
        <p className="text-xl font-bold" style={{ color: "#111" }}>{fmt(data.amount)}</p>
      </div>
    </div>

    {/* Line Items Table */}
    {data.line_items && data.line_items.length > 0 && (
      <div className="mb-8">
        <p className="text-xs uppercase tracking-wider font-semibold mb-2" style={{ color: "#999" }}>Line Items</p>
        <table className="w-full text-sm" style={{ borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "#f3f4f6" }}>
              <th className="text-left px-2 py-1.5 font-semibold" style={{ border: "1px solid #e5e7eb", width: "30px" }}>#</th>
              <th className="text-left px-2 py-1.5 font-semibold" style={{ border: "1px solid #e5e7eb" }}>Description</th>
              <th className="text-right px-2 py-1.5 font-semibold" style={{ border: "1px solid #e5e7eb", width: "60px" }}>Qty</th>
              <th className="text-center px-2 py-1.5 font-semibold" style={{ border: "1px solid #e5e7eb", width: "45px" }}>Unit</th>
              <th className="text-right px-2 py-1.5 font-semibold" style={{ border: "1px solid #e5e7eb", width: "85px" }}>Labor Rate</th>
              <th className="text-right px-2 py-1.5 font-semibold" style={{ border: "1px solid #e5e7eb", width: "85px" }}>Material Rate</th>
              <th className="text-right px-2 py-1.5 font-semibold" style={{ border: "1px solid #e5e7eb", width: "85px" }}>Unit Price</th>
              <th className="text-right px-2 py-1.5 font-semibold" style={{ border: "1px solid #e5e7eb", width: "95px" }}>Amount</th>
            </tr>
          </thead>
          <tbody>
            {data.line_items.map((li) => (
              <tr key={li.line_number}>
                <td className="px-2 py-1.5" style={{ border: "1px solid #e5e7eb", color: "#666" }}>{li.line_number}</td>
                <td className="px-2 py-1.5" style={{ border: "1px solid #e5e7eb" }}>{li.description}</td>
                <td className="text-right px-2 py-1.5 font-mono" style={{ border: "1px solid #e5e7eb" }}>{li.quantity.toLocaleString()}</td>
                <td className="text-center px-2 py-1.5" style={{ border: "1px solid #e5e7eb" }}>{li.unit}</td>
                <td className="text-right px-2 py-1.5 font-mono" style={{ border: "1px solid #e5e7eb" }}>{fmt(li.labor_unit_price)}</td>
                <td className="text-right px-2 py-1.5 font-mono" style={{ border: "1px solid #e5e7eb" }}>{fmt(li.material_unit_price)}</td>
                <td className="text-right px-2 py-1.5 font-mono" style={{ border: "1px solid #e5e7eb" }}>{fmt(li.unit_price)}</td>
                <td className="text-right px-2 py-1.5 font-mono font-semibold" style={{ border: "1px solid #e5e7eb" }}>{fmt(li.amount)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr style={{ background: "#f9fafb" }}>
              <td colSpan={7} className="text-right px-2 py-2 font-semibold" style={{ border: "1px solid #e5e7eb" }}>TOTAL</td>
              <td className="text-right px-2 py-2 font-mono font-bold" style={{ border: "1px solid #e5e7eb" }}>{fmt(data.amount)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    )}

    {/* Notes (formerly description) */}
    {(data.notes || data.description) && (
      <div className="mb-8">
        <p className="text-xs uppercase tracking-wider font-semibold mb-2" style={{ color: "#999" }}>Notes</p>
        <div className="p-4 rounded" style={{ background: "#f9fafb", border: "1px solid #e5e7eb" }}>
          <p style={{ color: "#111", whiteSpace: "pre-wrap" }}>{data.notes || data.description}</p>
        </div>
      </div>
    )}

    <div className="grid grid-cols-2 gap-8 mb-8">
      {data.job_order_number && (
        <div>
          <p className="text-xs uppercase tracking-wider font-semibold mb-1" style={{ color: "#999" }}>Linked Job Order</p>
          <p className="font-mono">{data.job_order_number}</p>
        </div>
      )}
      {data.work_order_number && (
        <div>
          <p className="text-xs uppercase tracking-wider font-semibold mb-1" style={{ color: "#999" }}>Linked Work Order</p>
          <p className="font-mono">{data.work_order_number}</p>
        </div>
      )}
    </div>

    {data.approved_at && (
      <div className="mb-8">
        <p className="text-xs uppercase tracking-wider font-semibold mb-1" style={{ color: "#999" }}>Approved</p>
        <p>{format(new Date(data.approved_at), "MMMM d, yyyy")}</p>
      </div>
    )}

    <div className="mt-16 grid grid-cols-2 gap-8">
      <div>
        <div className="border-b border-black w-full mb-1"></div>
        <p className="text-xs" style={{ color: "#666" }}>Authorized Signature</p>
      </div>
      <div>
        <div className="border-b border-black w-full mb-1"></div>
        <p className="text-xs" style={{ color: "#666" }}>Date</p>
      </div>
    </div>

    <CommandXFooter />
  </div>
);
